import {ref} from 'vue'

declare global {
  // eslint-disable-next-line no-var, no-unused-vars
  var atlas: any
}

const atlasScriptUrl = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.js'
const atlasCssHref = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.css'
const htmlMarkerPatchUrl = '/js/html-marker-layer-patch.js'
const atlasScriptAttrs: Record<string, string> = {
  integrity: 'sha384-ztWo4KfarMhMe1VioFgkKRIJD1NTYq1As2ufvNYIU+GfcmPvO6+SWucXHn/Q3jNf',
  crossorigin: 'anonymous'
}

const atlasLoadMax = 5
const htmlMarkerLoadMax = 12
const waitStepMs = 250

// Shared state across all composable instances
let atlasScriptPromise: Promise<boolean> | null = null
let htmlMarkerPromise: Promise<boolean> | null = null
let sharedMapClient: any = null

const ensureStylesheet = (href: string) => {
  if (import.meta.server) {return}
  const existing = document.querySelector(`link[href="${href}"]`)
  if (existing) {return existing}
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.type = 'text/css'
  link.href = href
  document.head.appendChild(link)
  return link
}

const waitForCondition = async (fn: () => boolean, timeoutMs: number, stepMs: number = waitStepMs) => {
  const start = Date.now()
  return new Promise<void>((resolve, reject) => {
    const check = () => {
      if (fn()) {
        resolve()
        return
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error('Condition not met within timeout'))
        return
      }
      setTimeout(check, stepMs)
    }
    check()
  })
}

const loadExternalScript = (src: string, attributes: Record<string, string> = {}) => {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      const atlasGlobalReady = src.includes('atlas.microsoft.com') && globalThis.atlas !== undefined
      const alreadyLoaded = existing.dataset.loaded === 'true' || (existing as any).readyState === 'complete' || (existing as any).readyState === 'loaded' || atlasGlobalReady
      if (alreadyLoaded) {
        existing.dataset.loaded = 'true'
        resolve(true)
        return
      }
      existing.addEventListener('load', () => resolve(true), {once: true})
      existing.addEventListener('error', () => {
        reject(new Error(`Failed to load script: ${src}`))
      }, {once: true})
      return
    }

    const script = document.createElement('script')
    script.src = src
    Object.entries(attributes).forEach(([key, value]) => script.setAttribute(key, value))
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true'
      resolve(true)
    }, {once: true})
    script.addEventListener('error', () => {
      reject(new Error(`Failed to load script: ${src}`))
    }, {once: true})
    document.head.appendChild(script)
  })
}

const waitForAtlasGlobal = async (remainingMs: number, atlasLoadAttempts: {value: number}): Promise<void> => {
  const atlasPresent = globalThis.atlas !== undefined
  if (atlasPresent) {
    atlasLoadAttempts.value = 0
    return
  }
  if (remainingMs <= 0) {
    throw new Error('Atlas global unavailable after waiting')
  }
  await new Promise(res => setTimeout(res, waitStepMs))
  return waitForAtlasGlobal(remainingMs - waitStepMs, atlasLoadAttempts)
}

const ensureHtmlMarkerLayerScript = async (htmlMarkerLoadAttempts: {value: number}) => {
  if (import.meta.server) {return false}
  if (htmlMarkerLoadAttempts.value >= htmlMarkerLoadMax) {
    htmlMarkerLoadAttempts.value = 0
  }

  for (let attempt = htmlMarkerLoadAttempts.value; attempt < htmlMarkerLoadMax; attempt += 1) {
    const atlas = globalThis.atlas
    if (atlas !== undefined && atlas?.layer?.HtmlMarkerLayer) {
      htmlMarkerLoadAttempts.value = 0
      return true
    }

    htmlMarkerLoadAttempts.value = attempt + 1

    // Load the patched html-marker-layer script
    await loadExternalScript(htmlMarkerPatchUrl)

    try {
      await waitForCondition(() => {
        return globalThis.atlas !== undefined && globalThis.atlas?.layer?.HtmlMarkerLayer
      }, 500 + 150 * (attempt + 1))
      htmlMarkerLoadAttempts.value = 0
      return true
    } catch {
      continue
    }
  }

  throw new Error('html-marker-layer script not available after retries')
}

export const useAzureAtlas = () => {
  const atlasReady = ref(false)
  const htmlMarkerReady = ref(false)
  const atlasLoadAttempts = ref(0)
  const htmlMarkerLoadAttempts = ref(0)

  const ensureAtlasScript = async () => {
    if (import.meta.server) {return false}
    ensureStylesheet(atlasCssHref)

    if (atlasLoadAttempts.value >= atlasLoadMax) {
      atlasLoadAttempts.value = 0
    }

    // Reset cached promise if atlas global went missing (e.g., after SPA navigation removed script)
    if (atlasScriptPromise && globalThis.atlas === undefined) {
      atlasScriptPromise = null
      atlasReady.value = false
    }

    atlasScriptPromise ??= (async () => {
      atlasLoadAttempts.value += 1
      await loadExternalScript(atlasScriptUrl, atlasScriptAttrs)
      await waitForAtlasGlobal(15000, atlasLoadAttempts)
      atlasReady.value = true
      atlasLoadAttempts.value = 0
      return true
    })().catch(err => {
      atlasScriptPromise = null
      throw err
    })

    return atlasScriptPromise
  }

  const ensureAtlasStack = async (apiKey: string) => {
    if (import.meta.server) {return}

    await ensureAtlasScript()

    // Reset cached promise if HtmlMarkerLayer went missing
    if (htmlMarkerPromise && !globalThis.atlas?.layer?.HtmlMarkerLayer) {
      htmlMarkerPromise = null
      htmlMarkerReady.value = false
    }

    htmlMarkerPromise ??= ensureHtmlMarkerLayerScript(htmlMarkerLoadAttempts).then(res => {
      htmlMarkerReady.value = true
      return res
    }).catch(err => {
      htmlMarkerPromise = null
      throw err
    })

    await htmlMarkerPromise

    if (!sharedMapClient && apiKey) {
      const MapsSearch = (await import('@azure-rest/maps-search')).default
      const {AzureKeyCredential} = await import('@azure/core-auth')
      sharedMapClient = MapsSearch(new AzureKeyCredential(apiKey))
    }
  }

  return {
    atlasReady,
    htmlMarkerReady,
    get mapClient() {
      return sharedMapClient
    },
    ensureAtlasStack
  }
}
